<!--@format-->
<template>
    <div class="form-details-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Form Template Details</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />
                <div v-else class="form-details-container">
                    <div v-if="formData.name" class="detail-container">
                        <label>Form Template Name</label>
                        <div>{{ formData.name }}</div>
                    </div>
                    <div v-if="formData.title" class="detail-container">
                        <label>Form Template Title</label>
                        <div>{{ formData.title }}</div>
                    </div>
                    <div v-if="formData.description" class="detail-container">
                        <label>Form Template Description</label>
                        <div>{{ formData.description }}</div>
                    </div>
                    <div v-if="formData.vendor" class="detail-container">
                        <label>Vendor</label>
                        <div class="profile-container">
                            <app-profile-image
                                :src="formData.vendor.logoUrl | companyLogoPlaceholder"
                            />
                            {{ formData.vendor.name }}
                        </div>
                    </div>
                    <div
                        v-if="formData.categories && formData.categories.length > 0"
                        class="detail-container"
                    >
                        <label>Categories</label>
                        <div class="categories-container">
                            <p
                                v-for="(category, idx) in formData.categories"
                                :key="`category-${idx}`"
                                class="category"
                            >
                                {{ category }}
                            </p>
                        </div>
                    </div>
                    <div v-if="!isClient" class="detail-container">
                        <label>Status</label>
                        <div
                            class="status-chip"
                            :class="formData.isPublished ? 'published' : 'unpublished'"
                        >
                            {{ formData.isPublished ? 'Published' : 'Unpublished' }}
                        </div>
                    </div>
                    <div v-if="formData.publishedTo" class="detail-container">
                        <label>{{
                            isAgency ? 'Partners published to' : 'Plans published to'
                        }}</label>
                        <div>{{ formData.publishedTo }}</div>
                    </div>
                    <div v-if="formData.uniqueViews" class="detail-container">
                        <label>Unique Views</label>
                        <div>{{ formData.uniqueViews }}</div>
                    </div>
                    <div v-if="formData.entries" class="detail-container">
                        <label>Entries</label>
                        <div>{{ formData.entries }}</div>
                    </div>
                    <div class="detail-container">
                        <label>Created Date</label>
                        <div>
                            <span class="weekday">{{
                                formData.createdAt | momentWeekDay | UpperCaseString
                            }}</span>
                            <span class="date">{{
                                formData.createdAt | momentDateMMMDYYYY | UpperCaseString
                            }}</span>
                        </div>
                    </div>
                    <div v-if="!isClient && formData.publishedAt" class="detail-container">
                        <label>Published Date</label>
                        <div>
                            <span class="weekday">{{
                                formData.publishedAt | momentWeekDay | UpperCaseString
                            }}</span>
                            <span class="date">{{
                                formData.publishedAt | momentDateMMMDYYYY | UpperCaseString
                            }}</span>
                        </div>
                    </div>
                    <div class="detail-container">
                        <label>Last Modified Date</label>
                        <div>
                            <span class="weekday">{{
                                formData.updatedAt | momentWeekDay | UpperCaseString
                            }}</span>
                            <span class="date">{{
                                formData.updatedAt | momentDateMMMDYYYY | UpperCaseString
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="closeWidget">Done</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { FormService } from '@/scripts/services/form/form-service';

const AppButton = () => import('@/components/common/app-button.vue');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

export default {
    name: 'form-details-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'app-profile-image': AppProfileImage,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,

            formData: {
                name: '',
                title: '',
                description: '',
                vendor: {},
                categories: [],
                createdAt: '',
                updatedAt: '',
            },
        };
    },

    computed: {
        isClient() {
            return !!this.$store.state.context.client.id;
        },

        isAgency() {
            return !!this.$store.state.context.agency.id;
        },
    },

    created() {
        this.getFormInfo();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getFormInfo() {
            this.isLoading = true;
            const requestConfig = {
                expand: 'Categories($select=Name)',
            };

            if (this.isClient) {
                requestConfig.expand =
                    requestConfig.expand +
                    ',Account($select=Id;$expand=Agency($select=Name,LogoImageUrl))';
            } else {
                requestConfig.expand = requestConfig.expand + ',SharedAssets($count=true;$top=1)';
            }

            return new Promise(() => {
                FormService.get(this.widget.formId, requestConfig)
                    .then(({ data: { value } }) => {
                        const [form] = value;
                        const isAgencyAvailable =
                            form.Account && form.Account.Agency && form.Account.Agency.Name;
                        let publishedDate = null;

                        if (form.PublishedAt) {
                            publishedDate = form.PublishedAt;
                        } else if (form['SharedAssets@odata.count'] > 0) {
                            publishedDate = form.SharedAssets[0].CreatedAt;
                        }

                        this.formData.name = form.Name;
                        this.formData.title = form.Title;
                        this.formData.description = form.Description;
                        this.formData.categories = form.Categories.map(({ Name }) => Name);
                        this.formData.isPublished =
                            form.IsPublished || form['SharedAssets@odata.count'] > 0;
                        this.formData.vendor = isAgencyAvailable
                            ? {
                                  name: form.Account.Agency.Name,
                                  logoUrl: form.Account.Agency.LogoImageUrl,
                              }
                            : null;
                        this.formData.publishedTo = form['SharedAssets@odata.count'];
                        this.formData.uniqueViews = +form.UniqueViews;
                        this.formData.entries = +form.Entries;
                        this.formData.createdAt = this.$moment(form.CreatedAt);
                        this.formData.publishedAt = publishedDate
                            ? this.$moment(publishedDate)
                            : null;
                        this.formData.updatedAt = this.$moment(form.UpdatedAt);
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your form details.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },
    },
};
</script>

<style>
.form-details-widget .form-details-container {
    border-radius: 16px;
    box-shadow: 0px 3px 12px #8274e833;
}

.form-details-widget .form-details-container .detail-container {
    padding: 16px 24px;
}

.form-details-widget .form-details-container .detail-container:nth-child(odd) {
    background-color: #f7f7f8;
}

.form-details-widget .detail-container .profile-container {
    align-items: center;
    display: flex;
    color: #18aeff;
}

.form-details-widget .detail-container .profile-container .app-profile-image .background-image {
    height: 28px;
    margin-right: 10px;
    width: 28px;
}

.form-details-widget .form-details-container .weekday {
    border-right: 2px solid #dcdbe0;
    margin-right: 10px;
    padding-right: 10px;
}

.form-details-widget .form-details-container .categories-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
}

.form-details-widget .form-details-container .category {
    background-color: #f7f7f8;
    border-radius: 18px;
    border: 1px solid #dcdbe0;
    display: flex;
    justify-content: center;
    margin: 0;
    padding: 7px 16px;
    text-align: center;
    width: fit-content;
    text-transform: capitalize;
}

.form-details-widget .form-details-container .status-chip {
    border-radius: 18px;
    font: normal normal 500 14px Roboto;
    padding: 8px 25px;
    width: fit-content;
}

.form-details-widget .form-details-container .published {
    background-color: #e2f4e3;
    color: #2ada2b;
}

.form-details-widget .form-details-container .unpublished {
    background-color: #eb7e0a33;
    color: #eb7e0a;
}

.form-details-widget .cancel-btn {
    background: transparent;
    border: none;
    color: #877d88;
}

.form-details-widget .cancel-btn:hover {
    background: #e1e0e5;
}
</style>
